import { FC, useEffect, useState } from 'react';
import moment from 'moment';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchClientCreditCards from '@/hooks/useApi/useFetchClientCreditCards';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';
import {
  Box,
  formattedCurrency,
  CreditCardTransactionResponse,
  ClientCreditCardLists,
  Card,
  withStyles,
  CardLogo,
  capitalizeFirstLetter,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type CreditCardTransactionInfoProps = {
  transaction: CreditCardTransactionResponse;
  isParent?: boolean;
};

type DataProps = {
  hasErrorInfo: boolean;
  failureReason: string;
};

const CreditCardTransactionInfo: FC<CreditCardTransactionInfoProps> = ({ transaction, isParent = false }) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.CreditCardTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { creditCards, fetchCreditCards } = useFetchClientCreditCards();
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const [isGetNonClientControlled, setIsGetNonClientControlled] = useState(false);
  const [creditCardsList, setCreditCardsList] = useState<{
    [idx: number]: ClientCreditCardLists;
  }>({});
  const creditCardInformation = creditCardsList[0] || {};

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: transaction.TransactionID,
    }).then(() => {
      if (transaction.CreditCardToken !== '' && !isGetNonClientControlled) {
        fetchCreditCards({
          PortalUser: '',
          ClientControlled: true,
          CreditCardToken: transaction.CreditCardToken,
        });

        setIsGetNonClientControlled(true);
      }
    });
  }, [transaction.TransactionID, currentDate]);

  useEffect(() => {
    if (isGetNonClientControlled) {
      fetchCreditCards({
        PortalUser: '',
        ClientControlled: false,
        CreditCardToken: transaction.CreditCardToken,
      });
      setIsGetNonClientControlled(false);
    }
    setCreditCardsList({ ...creditCardsList, ...creditCards });
  }, [creditCards]);

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.transactionDateTime', { defaultValue: 'Transaction Status' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('creditCard'),
    },
    {
      label: t('TransactionDetails.glCode', { defaultValue: 'GL Code' }),
      value: transaction.GLCode || '-',
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.notes', { defaultValue: 'Notes' }),
      value: transaction.Notes || '-',
    },
  ];

  const cardDetails = [
    {
      label: t('CardDetails.name', { defaultValue: 'Name' }),
      value:
        creditCardInformation?.CreditCardHolderName ||
        transactions
          .filter((x) => x.TransactionID === transaction.TransactionID)
          .map((data) => data.FullName)
          .pop() ||
        '-',
    },
    {
      label: t('CardDetails.cardNumber', { defaultValue: 'Card Number' }),
      value: (
        <Box ai="center" jc="fe" gapSize="5px">
          <CardLogo cardBrand={creditCardInformation?.CreditCardBrand || 'unknown'} width="24px" height="24px" />
          •••• {creditCardInformation?.CreditCardNumber?.slice(-4)}
        </Box>
      ),
      visible: !!creditCardInformation?.CreditCardNumber,
    },
    {
      label: t('CardDetails.brand', { defaultValue: 'Brand' }),
      value: t(`CardDetails.${creditCardInformation?.CreditCardBrand}`, {
        defaultValue: creditCardInformation?.CreditCardBrand,
      }),
    },
    {
      label: t('CardDetails.expiry', { defaultValue: 'Expiry' }),
      value: `${creditCardInformation?.CreditCardExpiryMonth || ''}/${creditCardInformation?.CreditCardExpiryYear || ''}`,
      visible: !!creditCardInformation?.CreditCardExpiryMonth,
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
    },
  ];

  const fullName =
    creditCardInformation.CreditCardHolderName ||
    transactions
      .filter((x) => x.TransactionID === transaction.TransactionID)
      .map((data) => data.FullName)
      .pop() ||
    '-';

  return (
    <Box flx="col" gapSize="24px">
      <TransactionCard
        receiverName={fullName}
        amount={transaction.Amount}
        currency={transaction.Currency}
        transactionId={transaction.TransactionID}
        status={transaction.TransactionStatus}
        type={'Credit Card'}
        transactionDate={transaction.TransactionDateTime}
        lastModified={transaction.LastModified}
        failureReason={state?.failureReason}
      />

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('cardDetailsTitle', { defaultValue: 'Card Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={cardDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Information' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default CreditCardTransactionInfo;
